/**
 * Ported VERBATIM from `apr_template/src/services/aiRunsApi.ts`.
 * Only import paths change. See ../compat/ for the shims.
 */
import api from './aprAxios';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AgentRun {
  id: string;
  name: string;
  description?: string;
  scope: 'portfolio' | 'subset';
  status: string;
  progress_pct: number;
  status_message?: string;
  error_message?: string;
  client_industry?: string;
  client_size?: string;
  framework_label?: string;
  /** How the analysis was produced: ai_generated | client_document | consolidated | manual */
  origin?: string;
  source_knowledge_file_id?: string;
  /** Additional Analysis Instructions (the analysis basis), on run detail. */
  additional_comments?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  total_input_tokens: number;
  total_output_tokens: number;
}

export interface AgentRunApp {
  id: string;
  application_id: string;
  application_id_code?: string;
  application_name: string;
  vendor?: string;
  excluded_from_apr?: boolean;
  status: string;
  /** Provenance of THIS per-app analysis: ai_generated | client_document | consolidated | manual */
  origin?: string;
  derived_from_ids?: string[];
  disposition?: string;
  mapped_framework_label?: string;
  rationale?: string;
  disposition_justification?: string;
  savings_pct_low?: number;
  savings_pct_mid?: number;
  savings_pct_high?: number;
  savings_low?: number;
  savings_mid?: number;
  savings_high?: number;
  current_annual_cost?: number;
  disposition_cost?: number;
  disposition_cost_low?: number;
  disposition_cost_high?: number;
  integration_cost?: number;
  integration_cost_low?: number;
  integration_cost_high?: number;
  total_one_time_cost?: number;
  total_one_time_cost_low?: number;
  total_one_time_cost_high?: number;
  disposition_timeline?: string;
  future_run_rate?: number;
  future_run_rate_justification?: string;
  methodology_note?: string;
  strategic_options?: string;
  confidence?: string;
  overridden: boolean;
  overridden_at?: string;
  override_note?: string;
  original_disposition?: string;
}

export interface AgentRunTheme {
  id: string;
  theme_type: string;
  title: string;
  summary?: string;
  estimated_savings_low?: number;
  estimated_savings_mid?: number;
  estimated_savings_high?: number;
  application_ids: string[];
  details_md?: string;
  sort_order: number;
}

export interface AgentRunActionPlanItem {
  id: string;
  application_id?: string;
  vendor?: string;
  section?: string;
  action_text: string;
  current_annual_cost?: number;
  savings_low?: number;
  savings_mid?: number;
  savings_high?: number;
  sort_order: number;
}

export interface AgentRunExecSummary {
  total_portfolio_cost?: number;
  total_in_scope_cost?: number;
  total_savings_low?: number;
  total_savings_mid?: number;
  total_savings_high?: number;
  total_one_time_cost?: number;
  headline_md?: string;
  body_md?: string;
  methodology_md?: string;
}

export interface AgentRunDetail extends AgentRun {
  apps: AgentRunApp[];
  themes: AgentRunTheme[];
  actionPlan: AgentRunActionPlanItem[];
  execSummary?: AgentRunExecSummary;
}

// ─── API Functions ───────────────────────────────────────────────────────────

export async function createRun(payload: any): Promise<AgentRun> {
  const response = await api.post('/ai/runs', payload);
  return response.data;
}

export async function listRuns(): Promise<AgentRun[]> {
  const response = await api.get('/ai/runs');
  return response.data;
}

export async function getRun(id: string): Promise<AgentRunDetail> {
  const response = await api.get(`/ai/runs/${id}`);
  return response.data;
}

export async function getProgress(id: string): Promise<{
  status: string;
  progress_pct: number;
  status_message?: string;
  apps_completed: number;
  apps_total: number;
}> {
  const response = await api.get(`/ai/runs/${id}/progress`);
  return response.data;
}

export async function cancelRun(id: string): Promise<void> {
  await api.post(`/ai/runs/${id}/cancel`);
}

// Administrator-only: permanently delete a run and all its results.
export async function deleteRun(id: string): Promise<void> {
  await api.delete(`/ai/runs/${id}`);
}

// Continue a cancelled/failed run — re-runs only the applications not yet done.
export async function resumeRun(id: string): Promise<void> {
  await api.post(`/ai/runs/${id}/resume`);
}

export interface RecognizeChild {
  id: string;
  application_id_code: string | null;
  name: string;
}
export interface RecognizeCandidate {
  id: string;
  application_id_code: string | null;
  name: string;
  description?: string;
  score: number;
  is_parent?: boolean;             // roll-up: not analyzable directly
  excluded_from_apr?: boolean;     // flagged "not relevant for rationalization"
  children?: RecognizeChild[];     // its analyzable children (when is_parent)
}
export interface RecognizeProposal {
  name: string; // the name extracted from the document
  candidates: RecognizeCandidate[];
}
export interface RecognizeMatch {
  id: string;
  application_id_code: string | null;
  name: string;
  matched_on: string;
  score?: number;
  is_parent?: boolean;
  excluded_from_apr?: boolean;
  children?: RecognizeChild[];
}
export interface RecognizeResult {
  document: { id: string; file_name: string };
  documents?: Array<{ id: string; file_name: string }>;
  extracted: number;
  matched: RecognizeMatch[];
  proposals: RecognizeProposal[];
  unmatched: string[];
}
// Extract application names from one or more knowledge documents + match to the portfolio.
export async function recognizeApps(documentIds: string | string[]): Promise<RecognizeResult> {
  const ids = Array.isArray(documentIds) ? documentIds : [documentIds];
  const response = await api.post('/ai/runs/recognize-apps', { documentIds: ids });
  return response.data;
}

export async function overrideApp(runId: string, appId: string, patch: any): Promise<AgentRunApp> {
  const response = await api.patch(`/ai/runs/${runId}/apps/${appId}`, patch);
  return response.data;
}

// ─── Client-provided analysis import ─────────────────────────────────────────
export type ReconcileMode = 'overwrite' | 'consolidate' | 'keep';
export interface ClientAnalysisTarget { applicationId: string; mode: ReconcileMode; }

// Continue an interrupted client-analysis run (e.g. after a server restart).
export async function resumeClientAnalysis(runId: string): Promise<void> {
  await api.post(`/ai/client-analysis/${runId}/resume`);
}

// Interpret a client-authored analysis document into structured per-app dispositions.
// Detect the mentioned apps first with recognizeApps(knowledgeFileId).
export async function importClientAnalysis(payload: {
  knowledgeFileId: string;
  name?: string;
  clientName?: string;
  additionalComments?: string;
  crossPortfolio?: boolean;
  apps: ClientAnalysisTarget[];
}): Promise<AgentRun> {
  const response = await api.post('/ai/client-analysis', payload);
  return response.data;
}

// ─── Import / Export Types ───────────────────────────────────────────────────

export interface ImportDiff {
  appRunId: string;
  applicationId: string;
  applicationName: string;
  changes: Array<{
    field: string;
    before: any;
    after: any;
  }>;
}

export interface ImportPreview {
  runId: string;
  exportedAt: string;
  summary: {
    appsWithChanges: number;
    totalFieldChanges: number;
    dispositionChanges: number;
    savingsChanges: number;
    justificationChanges: number;
    otherChanges: number;
  };
  warnings: string[];
  diffs: ImportDiff[];
}

// ─── Import / Export API Functions ──────────────────────────────────────────

export async function downloadXlsx(runId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/ai/runs/${runId}/export.xlsx`, {
    responseType: 'blob',
  });
  const disposition: string = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match ? (match[1] ?? '').replace(/['"]/g, '') : `analysis-run-${runId}.xlsx`;
  return { blob: response.data as Blob, filename };
}

export async function downloadPdf(runId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/ai/runs/${runId}/export.pdf`, {
    responseType: 'blob',
  });
  const disposition: string = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match ? (match[1] ?? '').replace(/['"]/g, '') : `analysis-run-${runId}.pdf`;
  return { blob: response.data as Blob, filename };
}

export async function downloadPptx(runId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/ai/runs/${runId}/export.pptx`, { responseType: 'blob' });
  const disposition: string = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match ? (match[1] ?? '').replace(/['"]/g, '') : `AI-Analysis-${runId}.pptx`;
  return { blob: response.data as Blob, filename };
}

export async function downloadDocx(runId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/ai/runs/${runId}/export.docx`, { responseType: 'blob' });
  const disposition: string = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const filename = match ? (match[1] ?? '').replace(/['"]/g, '') : `AI-Analysis-${runId}.docx`;
  return { blob: response.data as Blob, filename };
}

export async function previewImport(runId: string, file: File): Promise<ImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/ai/runs/${runId}/import.xlsx/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function applyImport(
  runId: string,
  preview: ImportPreview,
  note: string,
): Promise<{ applied: number; skipped: number; runId: string }> {
  const response = await api.post(`/ai/runs/${runId}/import.xlsx/apply`, { preview, note });
  return response.data;
}
